import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from './ErrorBoundary.tsx';
import App from './App.tsx';
import './index.css';
import { log } from './lib/logger';

const BUILD_VERSION = 'Mobile+PWA v1';
const BUILD_TIMESTAMP = new Date().toISOString();

log.info(`🚀 ${BUILD_VERSION}`, {
  builtAt: BUILD_TIMESTAMP,
  host: window.location.host,
  mode: import.meta.env.MODE,
  hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
  hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
  hasSW: 'serviceWorker' in navigator
});

// Unregister any existing service workers, then register only on non-auth routes.
(async () => {
  if ('serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) await r.unregister();
      log.debug('🧹 Service Worker unregistered');

      const path = window.location.pathname;
      if (path.startsWith('/auth/')) {
        log.debug('🛑 SW block: not registering service worker on', path);
      } else {
        await navigator.serviceWorker.register('/sw.js');
        log.debug('✅ Service Worker registered');
      }
    } catch (e) {
      log.warn('SW setup error', e);
    }
  }
})();

document.body.style.background = '#f1f5f9';
document.body.style.margin = '0';

const rootElement = document.getElementById('root');
if (!rootElement) {
  log.error('❌ Root element not found!');
  document.body.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: system-ui, sans-serif;">
      <div style="background: white; padding: 40px; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 600px;">
        <h1 style="color: #dc2626; margin: 0 0 16px 0;">⚠️ Critical Error</h1>
        <p style="color: #64748b; margin: 0;">Root element not found. The HTML structure is corrupt.</p>
        <button onclick="location.reload()" style="margin-top: 20px; background: #3b82f6; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer;">Reload Page</button>
      </div>
    </div>
  `;
} else {
  log.debug('✅ Root element found, rendering app...');

  try {
    createRoot(rootElement).render(
      <ErrorBoundary>
        <StrictMode>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </StrictMode>
      </ErrorBoundary>
    );
    log.debug('✅ App rendered successfully');
  } catch (error) {
    log.error('❌ Fatal error rendering app:', error);
    rootElement.innerHTML = `
      <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: system-ui, sans-serif; background: linear-gradient(to bottom right, #fee2e2, #fef2f2);">
        <div style="background: white; padding: 40px; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 600px;">
          <h1 style="color: #dc2626; margin: 0 0 16px 0;">❌ Fatal Render Error</h1>
          <p style="color: #64748b; margin: 0 0 16px 0;">The application failed to render.</p>
          <pre style="background: #fee2e2; padding: 12px; border-radius: 6px; font-size: 12px; overflow-x: auto;">${error}</pre>
          <div style="display: flex; gap: 12px; margin-top: 20px;">
            <button onclick="location.reload()" style="background: #3b82f6; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">Reload</button>
            <button onclick="localStorage.clear(); sessionStorage.clear(); location.reload()" style="background: #ef4444; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">Clear Cache & Reload</button>
          </div>
        </div>
      </div>
    `;
  }
}
